import { Component, DestroyRef, inject, NgZone } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatDialog } from "@angular/material/dialog";
import { NavigationStart, Router, RouterOutlet } from "@angular/router";
import { filter } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { ApplicationInsightsService } from "./core/services/application-insights.service";
import { NotificationService } from "./core/services/notification.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  imports: [RouterOutlet],
})
export class AppComponent {
  private readonly dialog = inject(MatDialog);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);

  constructor(private applicationInsightsService: ApplicationInsightsService) {
    this.ngZone.runOutsideAngular(() => this.bindVisualViewportToOverlays());

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart => event instanceof NavigationStart,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.dialog.closeAll();
        this.bottomSheet.dismiss();
        this.notificationService.closeConfirmBox();
      });

    try {
      this.applicationInsightsService.logEvent(
        `KCMS Pedemais ${environment.version}`,
      );
    } catch (error) {}
  }

  /**
   * Material overlays (bottom sheet) usam position:fixed no layout viewport.
   * No mobile o teclado reduz o visual viewport e cobre o sheet; sincronizar o
   * overlay ao visualViewport faz o sheet subir junto com o teclado.
   */
  private bindVisualViewportToOverlays(): void {
    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const rootStyle = document.documentElement.style;
    const sync = () => {
      rootStyle.setProperty("--visual-viewport-height", `${viewport.height}px`);
      rootStyle.setProperty("--visual-viewport-width", `${viewport.width}px`);
      rootStyle.setProperty(
        "--visual-viewport-offset-top",
        `${viewport.offsetTop}px`,
      );
      rootStyle.setProperty(
        "--visual-viewport-offset-left",
        `${viewport.offsetLeft}px`,
      );
    };

    viewport.addEventListener("resize", sync);
    viewport.addEventListener("scroll", sync);
    window.addEventListener("orientationchange", sync);
    sync();

    this.destroyRef.onDestroy(() => {
      viewport.removeEventListener("resize", sync);
      viewport.removeEventListener("scroll", sync);
      window.removeEventListener("orientationchange", sync);
    });
  }
}
